import React from 'react';
import { useT } from '../../i18n/useT';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import LegalPage, { LegalSection, LegalPlaceholder, LegalNote } from '../../components/legal/LegalPage';
import '../../components/legal/LegalPage.css';

export default function DataDeletionPage() {
  const t = useT();
  const lp = t.legalPages;
  return (
    <>
      <Helmet>
        <title>{lp.dataDeletion.title} – Docudi</title>
        <meta name="description" content={lp.dataDeletion.metaDesc} />
        <meta name="robots" content="noindex" />
      </Helmet>

      <LegalPage title={lp.dataDeletion.title} lastUpdated={null}>

        <LegalSection id="overview" title="Your Right to Erasure">
          <div className="legal-section__content">
            <p>
              Under Article 17 of the GDPR ("right to be forgotten"), you have the right to request the
              deletion of your personal data from our systems, subject to certain legal obligations we may
              have to retain it.
            </p>
            <p>
              This page explains how to delete your account and personal data, what data gets deleted, what
              may be retained, and how long the process takes.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="how-to-delete" title="How to Delete Your Account">
          <div className="legal-section__content">
            <h3>Option 1: Delete directly in the app</h3>
            <ol>
              <li>Open the Docudi app and sign in to your account.</li>
              <li>Go to <strong>Settings</strong> → <strong>Account</strong>.</li>
              <li>Scroll to the bottom and tap <strong>"Delete Account"</strong>.</li>
              <li>Confirm your password and tap <strong>"Permanently Delete"</strong>.</li>
            </ol>
            <p>Your account and data will be scheduled for deletion immediately.</p>

            <h3>Option 2: Submit a deletion request by email</h3>
            <p>
              If you cannot access your account, send an email to{' '}
              <a href={`mailto:${'[PLACEHOLDER: privacy@example.com]'}`}>
                <LegalPlaceholder label="privacy@example.com" />
              </a>{' '}
              with the subject line <strong>"Data Deletion Request"</strong>. Include:
            </p>
            <ul>
              <li>The email address associated with your account</li>
              <li>A brief description of your request</li>
              <li>Proof of identity if required (we may ask to verify you are the account holder)</li>
            </ul>

            <h3>Option 3: Contact form</h3>
            <p>
              You can also use our <Link to="/contact">contact form</Link> and select "Data / Privacy" as
              the subject. We will respond within <LegalPlaceholder label="X business days" />.
            </p>

            <LegalNote>
              Under Art. 12 GDPR, we must respond to data subject requests without undue delay and in any
              event within one calendar month of receipt. The deadline may be extended by up to two further
              months for complex requests.
            </LegalNote>
          </div>
        </LegalSection>

        <LegalSection id="what-gets-deleted" title="What Gets Deleted">
          <div className="legal-section__content">
            <p>When you delete your account, the following data is permanently removed:</p>
            <ul>
              <li>Your account profile (name, email address)</li>
              <li>All documents and scanned files you uploaded</li>
              <li>Folders, tags, and document metadata</li>
              <li>App settings and preferences</li>
              <li>Synced data across all devices</li>
            </ul>
            <p>
              Deletion of files from our storage infrastructure may take up to{' '}
              <LegalPlaceholder label="X days" /> due to backup rotation schedules.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="what-is-retained" title="What May Be Retained">
          <div className="legal-section__content">
            <p>
              Certain data may be retained for limited periods after account deletion to comply with legal obligations:
            </p>
            <ul>
              <li>
                <strong>Billing records and invoices:</strong> Retained for{' '}
                <LegalPlaceholder label="X years" /> as required by{' '}
                <LegalPlaceholder label="§ 257 HGB / applicable tax law" />.
              </li>
              <li>
                <strong>Fraud and security logs:</strong> Retained for{' '}
                <LegalPlaceholder label="X months" /> to detect and prevent abuse.
              </li>
              <li>
                <strong>Legal holds:</strong> Data subject to an active legal investigation or dispute may
                be retained until the matter is resolved.
              </li>
            </ul>
            <p>
              This retained data is isolated and only used for the specific legal purpose — it is not used
              for service provision, analytics, or marketing.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="data-export" title="Export Your Data First">
          <div className="legal-section__content">
            <p>
              Before deleting your account, you can export all your data. Deletion is permanent and
              cannot be undone.
            </p>
            <ul>
              <li>In the app: <strong>Settings → Account → Export My Data</strong></li>
              <li>By email: Contact <LegalPlaceholder label="privacy@example.com" /> with subject "Data Export Request"</li>
            </ul>
            <p>
              Exports are delivered as a <LegalPlaceholder label="ZIP archive / JSON file" /> and include
              all your documents and metadata. Processing may take up to{' '}
              <LegalPlaceholder label="X business days" />.
            </p>
          </div>
        </LegalSection>

        <LegalSection id="timeline" title="Timeline">
          <div className="legal-section__content">
            <ul>
              <li><strong>Immediate:</strong> Account access is revoked and your data is no longer visible.</li>
              <li><strong>Within <LegalPlaceholder label="X days" />:</strong> Documents and user data are deleted from primary storage.</li>
              <li><strong>Within <LegalPlaceholder label="X days" />:</strong> Data is purged from all backup systems.</li>
              <li><strong>Confirmation:</strong> You will receive an email confirmation when the deletion is complete.</li>
            </ul>
          </div>
        </LegalSection>

        <LegalSection id="contact" title="Contact Us">
          <div className="legal-section__content">
            <p>
              For any data deletion or privacy questions, contact our Data Privacy team:<br />
              Email: <LegalPlaceholder label="privacy@example.com" /><br />
              <LegalPlaceholder label="Response time: X business days" />
            </p>
            <p>
              You also have the right to lodge a complaint with your local supervisory authority if you
              believe your request has not been handled correctly. See our{' '}
              <Link to="/privacy">Privacy Policy</Link> for more details.
            </p>
          </div>
        </LegalSection>

      </LegalPage>
    </>
  );
}
